import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import type { ReviewId } from "../data/review-data"

/** One version in the review's thread — the same label, resubmitted. */
export interface ReviewVersionWire {
  id: number
  version_no: number
  fpl_document_id: number | null
  created_at: string
}

export interface ReviewVersion {
  reviewId: ReviewId
  /** Display form, matching the rest of the console: `v3`. */
  label: string
  versionNo: number
  /** Empty when the version has no printed label to show. */
  fplUrl: string
}

export function toVersion(w: ReviewVersionWire): ReviewVersion {
  return {
    reviewId: String(w.id) as ReviewId,
    label: `v${String(w.version_no)}`,
    versionNo: w.version_no,
    fplUrl: w.fpl_document_id != null ? `/api/v1/documents/${String(w.fpl_document_id)}/file` : "",
  }
}

/** Newest first, dropping any version whose document never landed — it would offer a blank pane. */
export function selectableVersions(wire: readonly ReviewVersionWire[]): ReviewVersion[] {
  return wire
    .map(toVersion)
    .filter((v) => v.fplUrl !== "")
    .sort((a, b) => b.versionNo - a.versionNo)
}

export function useReviewVersionsQuery(reviewId: string | null): UseQueryResult<ReviewVersion[]> {
  return useQuery({
    queryKey: qk.reviews.versions(reviewId ?? "none"),
    queryFn: async ({ signal }) => {
      const wire = await http.get<ReviewVersionWire[]>(
        `/api/v1/reviews/${encodeURIComponent(reviewId ?? "")}/versions`,
        { signal },
      )
      return selectableVersions(wire)
    },
    enabled: reviewId !== null,
  })
}
